import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface CitationPreferences {
  sortBy: 'manuscript' | 'author' | 'date' | 'title' | 'type';
  sortDirection: 'asc' | 'desc';
  filterSourceType: string | null;
  filterCitationType: 'all' | 'quote' | 'paraphrase';
  filterAssignment: 'all' | 'assigned' | 'unassigned';
}

const DEFAULT_PREFERENCES: CitationPreferences = {
  sortBy: 'manuscript',
  sortDirection: 'asc',
  filterSourceType: null,
  filterCitationType: 'all',
  filterAssignment: 'all',
};

export function useCitationPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<CitationPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const savedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load preferences from profile settings
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const loadPreferences = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('settings')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error loading citation preferences:', error);
          setIsLoading(false);
          return;
        }

        const settings = data?.settings as Record<string, unknown> | null;
        if (settings?.citationPreferences) {
          setPreferences({
            ...DEFAULT_PREFERENCES,
            ...(settings.citationPreferences as Partial<CitationPreferences>),
          });
        }
      } catch (err) {
        console.error('Error loading citation preferences:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  // Save preferences to profile settings with debouncing
  const savePreferences = useCallback(async (newPrefs: Partial<CitationPreferences>) => {
    if (!user) return;

    const updatedPrefs = { ...preferences, ...newPrefs };
    setPreferences(updatedPrefs);
    setIsSaving(true);
    setJustSaved(false);

    // Clear any pending timeouts
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }

    // Debounce the actual save to avoid too many requests
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Get existing settings first
        const { data: existingData } = await supabase
          .from('profiles')
          .select('settings')
          .eq('id', user.id)
          .single();

        const existingSettings = (existingData?.settings as Record<string, unknown>) || {};

        // Merge with new citation preferences
        const { error } = await supabase
          .from('profiles')
          .update({
            settings: {
              ...existingSettings,
              citationPreferences: updatedPrefs,
            },
            updated_at: new Date().toISOString(),
          })
          .eq('id', user.id);

        if (error) {
          console.error('Error saving citation preferences:', error);
        } else {
          // Show "Saved" briefly
          setJustSaved(true);
          savedTimeoutRef.current = setTimeout(() => {
            setJustSaved(false);
          }, 1500);
        }
      } catch (err) {
        console.error('Error saving citation preferences:', err);
      } finally {
        setIsSaving(false);
      }
    }, 500);
  }, [user, preferences]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, []);

  // Helper to update a single preference
  const updatePreference = useCallback(<K extends keyof CitationPreferences>(
    key: K,
    value: CitationPreferences[K]
  ) => {
    savePreferences({ [key]: value });
  }, [savePreferences]);

  // Reset to defaults
  const resetPreferences = useCallback(() => {
    savePreferences(DEFAULT_PREFERENCES);
  }, [savePreferences]);

  return {
    preferences,
    isLoading,
    isSaving,
    justSaved,
    updatePreference,
    savePreferences,
    resetPreferences,
  };
}
